/**
 * Event Payload Schemas
 *
 * Zod schemas for validating Socket.io event payloads.
 * Use these at trust boundaries to validate incoming data.
 */

import { z } from 'zod';
import {
  SessionIdSchema,
  AgentIdSchema,
  SdkSessionIdSchema,
  PermissionModeSchema,
  ImageAttachmentSchema,
  FileAttachmentSchema,
  TodoSchema,
  SerializedSessionSchema,
} from './common.schema.js';
import { NoticeBlockSchema } from './content-blocks.schema.js';
import { PlanHeadsUpReadingSchema } from './plan-heads-up.schema.js';
import { AgentToolUsePayloadSchema, AgentToolResultPayloadSchema } from './tool-payloads.schema.js';
import { ENGINE_SIGN_IN_METHOD_IDS, STANDING_STATUSES, STANDING_WINDOW_KINDS, SUBSCRIPTION_ENGINES } from '../engines/standing.js';
import { SlotIdSchema } from '../engines/accounts.js';
import { ENGINE_CONNECT_FAILURES, ENGINE_CONNECT_PHASES } from '../engines/connect.js';
import { HARNESS_IDS } from '../harness/ids.js';

import { SurfaceSchema } from './agent-trace.schema.js';
import { TurnOutcomeSchema } from './turn-outcome.schema.js';
import type { TerminalReasonView } from '../types/terminal-reason.js';

// NOTE: the { name → schema } EventSchemas map that used to live at the bottom
// of this file now lives in ../events/registry.ts (the single authored wire
// contract), which imports these domain payload schemas directly from their
// source files. This file only DEFINES schemas; it no longer assembles the map.

// =============================================================================
// Session Events
// =============================================================================

export const SessionCreatePayloadSchema = z.object({
  agentId: AgentIdSchema.optional(),
  permissionMode: PermissionModeSchema.optional(),
});

export const SessionCreatedPayloadSchema = z.object({
  session: SerializedSessionSchema,
});

export const SessionListPayloadSchema = z.object({
  sessions: z.array(SerializedSessionSchema),
});

export const SessionResumePayloadSchema = z.object({
  sessionId: SessionIdSchema,
});

export const SessionUpdatePayloadSchema = z.object({
  session: SerializedSessionSchema,
});

export const SessionDestroyPayloadSchema = z.object({
  sessionId: SessionIdSchema,
});

export const SessionConfigPayloadSchema = z.object({
  permissionMode: PermissionModeSchema.optional(),
  agentId: AgentIdSchema.optional(),
});

// =============================================================================
// Agent Registration Events
// =============================================================================

export const AgentRegisterPayloadSchema = z.object({
  sessionId: SessionIdSchema,
  agentId: AgentIdSchema,
});

// =============================================================================
// User Message Events
// =============================================================================

// K6 A0 — queue lane. Kept in lockstep with the staging-payloads.schema.ts copy
// (deliberately NOT derived — see that file's note) and the harness.ts MessagePriority
// TS mirror. Enum, so it parses byte-identically across both copies.
const MessagePrioritySchema = z.enum(['now', 'next', 'later']);

// Two-brain P2 — which SURFACE a send was born from (routing signal for capability
// placement; provenance stays in `origin`). Additive-optional: absent = legacy
// client = treated as 'web' by the router. Kept in lockstep with the
// staging-payloads.schema.ts copy (deliberately NOT derived — see that file's note).
const TaskSurfaceSchema = z.enum(['web', 'native', 'channel']);

// P4 — MessageOrigin as a runtime discriminated union. Kept in lockstep with the
// staging-payloads.schema.ts copy (deliberately NOT derived — see that file's note)
// and the contract/harness.ts TS mirror. The four legacy kinds parse byte-identically;
// the `initiative` variant carries WHY it fired (`trigger`) + how loudly it may surface
// (`politeness`). A trigger can exist ONLY on an initiative origin.
const InitiativeTriggerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('scheduled'), scheduleId: z.string(), firedAt: z.string() }),
  z.object({ kind: z.literal('signal'), source: z.string(), eventId: z.string().optional() }),
  z.object({ kind: z.literal('context'), reason: z.string(), score: z.number().optional() }),
]);
const MessageOriginSchema = z.discriminatedUnion('kind', [
  // P5.1 (Agent Teams) — peerName/peerTaskId are additive-optional peer
  // attribution, meaningful only for kind:'peer'. Absent ⇒ byte-identical.
  z.object({
    kind: z.enum(['human', 'channel', 'peer', 'auto-continuation']),
    peerName: z.string().optional(),
    peerTaskId: z.string().optional(),
  }),
  z.object({
    kind: z.literal('initiative'),
    trigger: InitiativeTriggerSchema,
    politeness: z.enum(['may-notify', 'silent-unless-actionable', 'draft-only']),
  }),
]);

export const UserMessagePayloadSchema = z.object({
  // Client-Generated ID Flow (Phase 1 — backwards compatible):
  // - New clients: always provide taskId (client-generated UUID v4). correlationId omitted.
  // - Legacy clients: omit taskId (or send null). Server falls back to generating a UUID
  //   and echoes correlationId in TASK_CREATED for client-side matching.
  // - Phase 3 (future PR) will make taskId required and remove correlationId entirely.
  sessionId: SessionIdSchema.optional(),  // @deprecated Use taskId instead
  taskId: z.string().nullable().optional(),  // Client-provided UUID for new tasks, or existing taskId for continue
  correlationId: z.string().optional(),  // LEGACY — removed in Phase 3. Only used by legacy clients for ack matching.
  // Trace correlation id for this user-send (one agent run). Additive-optional;
  // minted at the web edge, threaded server→sandbox, keys the agent_turns row.
  traceId: z.string().optional(),
  // OTEL_TRACES_V1, server→sandbox only: the W3C traceparent of the turn this opens (types/trace-context.ts).
  traceparent: z.string().max(55).optional(),
  id: z.string().optional(),  // Message ID for queue edit/cancel support
  message: z.string(),
  images: z.array(ImageAttachmentSchema).optional(),
  files: z.array(FileAttachmentSchema).optional(),
  // Target agent slug - which agent should receive this message
  // If not provided, server falls back to @mention parsing or session-manager
  targetAgent: z.string().optional(),
  // Agent mode — maps to model tier (light/pro/max)
  mode: z.enum(['light', 'pro', 'max']).optional(),
  // Harness runtime for this task. ACCEPTED-AND-IGNORED: the server never reads
  // this value. A new web task is stored with the user's ENGINE PREFERENCE
  // (resolveStoredRuntime → coerceCreationRuntime, which admits 'native' and —
  // for an enabled user — 'claude_code'); an existing task launches on the engine
  // locked on its row (resolveLaunchRuntime). The enum still admits the legacy
  // ids so stale client bundles and replayed historical events stay schema-valid
  // rather than failing validation. Optional; server defaults native.
  runtime: z.enum(HARNESS_IDS).optional(),
  // Several accounts per engine (spec 2026-10-01): the slot (the account's own config dir on the
  // box) a claude_code turn runs on. The SERVER mints it per turn (pickSlot) on its emit to the
  // box; a client's value is never forwarded. Absent ⇒ 'primary', today's one account.
  engineSlotId: SlotIdSchema.optional(),
  // Per-task model id chosen in the composer (e.g. claude-opus-4-8, gpt-5.5,
  // glm-5.2). Additive-optional ⇒ existing clients (no model) stay valid and
  // byte-identical; server derives the runtime from this model's provider.
  model: z.string().optional(),
  // Silent: forward to agent but don't persist to DB or broadcast to other tabs
  silent: z.boolean().optional(),
  // K6 P5: client-minted envelope identity. uuid rides ALL arms (additive pure
  // identity); origin is provenance. shouldQuery stays arm-gated in the sandbox via
  // envelopeForArm and is not on the FE wire.
  uuid: z.string().optional(),
  origin: MessageOriginSchema.optional(),
  // Two-brain P2 — surface the send was born from ('web' | 'native' | 'channel').
  // Additive-optional: absent ⇒ legacy client, router treats as 'web'.
  surface: TaskSurfaceSchema.optional(),
  // OD2 (omni-observability) — OPTIONAL fine-grained surface for the trace
  // dimension ONLY (agent_turns.surface). Never consulted for runner placement;
  // that stays on the coarse `surface` above. E.g. the extension sends
  // surface:'web' (placement unchanged) + traceSurface:'extension'. Absent ⇒
  // the server derives from the coarse field (native→'native-app', web→'web').
  traceSurface: SurfaceSchema.optional(),
  // P1.1 (control-plane): queue lane on the FE wire. A typed mid-turn send carries
  // `priority: 'now'` so the sandbox steers the SAME turn at the next tool-round
  // boundary (staged-lane + pollSteer consume it). Additive-optional ⇒ absent priority
  // keeps every existing payload byte-identical.
  priority: MessagePrioritySchema.optional(),
  // Phase 3 intelligent ingest: client hint for deposit vs task routing.
  // Optional — omitting is equivalent to 'auto'. Gated behind ENABLE_INTELLIGENT_INGEST;
  // server ignores this field when the flag is off (byte-identical existing path).
  intent: z.enum(['deposit', 'task', 'auto']).optional(),
  // K6 P6 flagship: a dedicated create-routine run. `purpose` marks the run; the
  // server attaches `outputFormat` (RoutineSpecSchema as a json_schema OutputFormat)
  // so the harness constrains the FINAL answer the server then feeds to
  // createRoutine(). `outputFormat` is validated structurally by the kernel/
  // transport, not on this socket boundary — z.unknown() keeps the schema
  // additive-optional so all existing clients stay valid.
  purpose: z.literal('create_routine').optional(),
  outputFormat: z.unknown().optional(),
  // Agents first (train 1c): the durable agent this task runs as. Server-injected on the
  // server to sandbox frame only; absent for every Bash task and from every client frame,
  // so old producers and consumers keep validating and the box stays byte-identical.
  agent: z.object({ id: z.string(), handle: z.string(), name: z.string() }).optional(),
});

export const UserMessageToAgentPayloadSchema = z.object({
  message: z.string(),
  images: z.array(ImageAttachmentSchema).optional(),
  files: z.array(FileAttachmentSchema).optional(),
  // Resolved model ID from mode (set by server before forwarding to agent)
  model: z.string().optional(),
  // Trace correlation id threaded from the web edge through to the sandbox so
  // journal rows + spans in the sandbox can be tied back to the agent_turns row.
  traceId: z.string().optional(),
});

// =============================================================================
// Agent Lifecycle Events
// =============================================================================

// AGENT_START / AGENT_END / AGENT_RESULT / AGENT_TODO describe the WIRE: the native runner
// (apps/sandbox run-driver.ts, buildAgentResultPayload, harness-sink.ts, board-seam.ts) sends
// `taskId` and no `sessionId` (the required sessionId was the deleted Claude arm's shape and
// failed the forward parse gate, relay/forward-parse.ts, on every staging turn until 2 Oct).
// The relay routes on `taskId ?? sessionId`, so both keys are optional.
const NativeTaskKeys = { sessionId: SessionIdSchema.optional(), taskId: z.string().min(1).optional() };
export const AgentStartPayloadSchema = z.object({
  ...NativeTaskKeys,
  /**
   * `true` ONLY when this AGENT_START begins a genuinely NEW USER TURN (fresh
   * human message). Tells the web to open a fresh assistant bubble instead of
   * merging into the trailing committed assistant message. Absent → legacy merge
   * (fail-safe; arm-neutral).
   */
  newTurn: z.boolean().optional(),
  /** PR-D kickoff span — see AgentStartPayload.warmup. Optional; absent ⇒ legacy wire. */
  warmup: z
    .object({
      gateWaitMs: z.number(),
      abilitiesMs: z.number(),
      journalMs: z.number(),
      toolsMs: z.number(),
      prologueMs: z.number(),
      totalMs: z.number(),
      cold: z.boolean(),
    })
    .optional(),
  /**
   * The turn's own id (turn reattach rail, spec 3.3), minted by the runner as it opens the
   * turn; absent when the rail is off. The web records it, and once a later START replaces
   * it, refuses an AGENT_END that names it (streamingHandlers.ts, #4130 follow-up).
   */
  turnId: z.string().optional(),
});

export const AgentEndPayloadSchema = z.object({
  ...NativeTaskKeys,
  sdkSessionId: z.string().optional(),
  /** @deprecated Use `outcome.kind` instead. Kept for backward compatibility. */
  stopReason: z.string().optional(),
  /**
   * Structured termination outcome. Present on all AGENT_END events emitted
   * by sandbox builds after 2026-04-19. See docs/.../terminal-reason-design.md.
   */
  outcome: TurnOutcomeSchema.optional(),
  /**
   * K6 P4 (native/openai only): user-facing terminal reason mapped from
   * `outcome` via terminalReasonFor(). Present iff the harness is
   * non-cost-reporting (native/openai); the Claude SDK arm never sets it.
   * DECLARED here (not just on the TS type) so the schema — the single author
   * of AGENT_END — fully describes the wire and z.infer never strips it.
   * A pass-through of the agent-sdk TerminalReasonView (z.custom → no re-derived
   * structural copy to drift); AGENT_END is forwarded raw by the relay and never
   * `.parse()`d on a live path, so this is additive and runtime-neutral.
   */
  terminalReason: z.custom<TerminalReasonView>().optional(),
  /** SDK assistant-message uuid for this turn. */
  sdkMessageUuid: z.string().uuid().optional(),
  /**
   * Server-assigned DB row id for the assistant message this turn
   * produced. Injected by the relay (not the sandbox). Frontend uses it
   * to reconcile an optimistic message's identity → persisted, so future
   * per-message events (MESSAGE_CREDIT_UPDATE, thumbs, etc.) can find
   * the right row without string-matching fallbacks. Optional for
   * defensive reasons — older relays may not emit it.
   */
  messageId: z.string().uuid().optional(),
  /**
   * TRUE iff this AGENT_END finalizes a turn that ended because the agent PAUSED
   * for human input (B-core's pause-ends-run). A suspension, not a completion —
   * the binding interaction is still pending and the run resumes on resolve.
   * DECLARED here (matches `AgentEndPayload.paused`) so the schema never silently
   * strips it from a validated/parsed AGENT_END payload.
   */
  paused: z.boolean().optional(),
  /** Several accounts (spec 2026-10-01): 'switched' = the turn ended rate_limited on the person's plan and the next account carried the task
   *  on, so it is no failure; planLimitNetAt = the exact resume_at the switch's net armed, the ONE resumeAt the web ignores (server:
   *  agent-end-lifecycle markSwitchedTurn; the turn's turn_footer carries both for a reload). Absent on every other turn. */
  planLimit: z.literal('switched').optional(),
  planLimitNetAt: z.string().optional(),
  /**
   * The id of the turn this END closes, the one its AGENT_START carried (see
   * AgentStartPayloadSchema.turnId): the runner's own END carries it, and so does the server's
   * reconcile END (agentTrace.getTurnId). Absent = an END that names no turn, which the web
   * accepts as before.
   */
  turnId: z.string().optional(),
});

export const AgentErrorPayloadSchema = z.object({
  sessionId: SessionIdSchema.optional(),
  error: z.string(),
});

export const AgentInterruptPayloadSchema = z.object({
  sessionId: SessionIdSchema,
  targetAgent: z.string().optional(), // Which agent to interrupt (for multi-agent sessions)
  /**
   * Who pulled it: the Esc key or the Stop button. The runner grants Esc a short
   * grace after a steer (a second Esc that lands on an already-drained queue was
   * a SEND gesture, not a stop — founder 08-31); the button always stops. Absent
   * (older clients) reads as the button.
   */
  source: z.enum(['esc', 'button']).optional(),
});

// =============================================================================
// Workspace files (runner → server)
// =============================================================================

/**
 * The box's own report that it wrote a file under /app/workspace/files. It replaces the
 * server's `sandbox.fs.watch`, an inbound stream that billed the sandbox awake for as long
 * as it was open (spec 2026-09-06). Shaped to match the server's UserFileWatchEvent exactly,
 * so the relay hands it straight to handleUserFileWatchEvent with no translation.
 *
 * A removal is deliberately not a case here: the library is the source of truth and files/
 * is its mirror, so a file missing from the mirror is drift, never a decision to delete.
 */
export const FilesChangedPayloadSchema = z.object({
  op: z.enum(['create', 'write']),
  /** Just the file name, e.g. `report.pdf`. */
  filename: z.string().min(1),
  /** Absolute path inside the box. */
  filePath: z.string().min(1),
  /** Path under the files root, e.g. `downloads/report.pdf`. */
  relativePath: z.string().min(1).optional(),
});

// =============================================================================
// SDK System Events
// =============================================================================

export const SystemInitPayloadSchema = z.object({
  sessionId: SessionIdSchema.optional(),  // @deprecated Use correlationId for matching
  sdkSessionId: SdkSessionIdSchema,
  agentSlug: z.string().optional(), // Which agent emitted this (for task creation)
  correlationId: z.string().optional(), // Single ID Refactor: For matching pending messages
});

export const AgentResultPayloadSchema = z.object({
  ...NativeTaskKeys,
  result: z.unknown(),
  subtype: z.string().optional(),
  structured_output: z.unknown().optional(),
  // P1.7 — SANDBOX-AUTHORITATIVE per-turn delta. The native-family engine computes the
  // per-turn cost delta internally (`cumulativeCostUsd_end − turnStartCumulative`, with any
  // journal seed folded into turn-start) and rides it here; the server stashes it DIRECTLY
  // instead of reconstructing the delta from the cumulative. `turnDeltaAuthoritative` gates
  // the direct-stash path — absent (the Claude arm + native $0 edge paths) → the server
  // falls back to its cumulative→delta reconstruction, byte-identical. Additive/optional.
  turnDeltaUsd: z.number().optional(),
  turnDeltaAuthoritative: z.literal(true).optional(),
  // P4 Task 4 — additive classification of a SELF-INITIATED turn's result. The engine
  // stamps `turn_end.initiative`; query-manager rides it onto AGENT_RESULT with
  // `turnClass:'ambient'`. Both omitted on every human/default turn → byte-identical.
  // Delivery (panel/toast/voice) is product-plane, downstream of this event.
  initiative: z
    .object({
      trigger: InitiativeTriggerSchema,
      politeness: z.enum(['may-notify', 'silent-unless-actionable', 'draft-only']),
    })
    .optional(),
  turnClass: z.literal('ambient').optional(),
});

// =============================================================================
// Content Block Events
// =============================================================================

export const AgentThinkingPayloadSchema = z.object({
  sessionId: SessionIdSchema,
  thinking: z.string(),
  isPartial: z.boolean().optional(),
});

export const AgentThinkingDeltaPayloadSchema = z.object({
  sessionId: SessionIdSchema,
  thinking: z.string(),
});

/** Thinking OPENED (no text yet) — mounts the timer pill before the first delta. */
export const AgentThinkingStartPayloadSchema = z.object({
  sessionId: SessionIdSchema,
  taskId: z.string().optional(),
});

export const AgentTextPayloadSchema = z.object({
  sessionId: SessionIdSchema,
  text: z.string(),
  isPartial: z.boolean().optional(),
});

export const AgentTextDeltaPayloadSchema = z.object({
  sessionId: SessionIdSchema,
  text: z.string(),
});

// agent:tool_use / agent:tool_result live in tool-payloads.schema.ts (every field the sink sends).
export { AgentToolUsePayloadSchema, AgentToolResultPayloadSchema };

// =============================================================================
// Agent Todo
// =============================================================================
// Phase 9: PendingQuestion / QuestionAnswer / PendingApproval / ApprovalResponse
// / PermissionRequest / PermissionResponse Zod schemas deleted — superseded by
// the unified InteractionRequestSchema / InteractionOutcomeSchema (see
// schemas/interaction.schema.ts) routed via INTERACTION_REQUIRED / RESPONSE.

export const AgentTodoPayloadSchema = z.object({
  ...NativeTaskKeys,
  todos: z.array(TodoSchema),
});

/** Agents first (train 1c): an agent's notebook file, mirrored at a turn boundary. */
export const AgentNotebookPayloadSchema = z.object({
  taskId: z.string(),
  agentId: z.string().min(1),
  handle: z.string().min(1),
  snapshot: z.string(),
  bytes: z.number().int().nonnegative(),
});

// =============================================================================
// Session Observability (Phase 2)
// =============================================================================

export const SessionCompactedPayloadSchema = z.object({
  taskId: z.string(),
  trigger: z.enum(['manual', 'auto']),
  summary: z.string(),
});

// =============================================================================
// Per-turn credit cost
// =============================================================================

/**
 * Server → frontend: per-turn credit cost resolved from SDK's total_cost_usd.
 * Frontend merges `creditCost` into the identified message's turn_footer block.
 */
export const MessageCreditUpdatePayloadSchema = z.object({
  taskId: z.string(),
  messageId: z.string(),
  creditCost: z.number().nonnegative(),
});

// =============================================================================
// Intelligent Ingest — Deposit Ack (Phase 3)
// =============================================================================

/**
 * Server → frontend: a user message was classified as a deposit and durably
 * captured. No assistant turn will follow — the frontend must NOT seed an
 * optimistic loader. The `label` is a short human-readable summary of what
 * was stored (e.g. "Note saved" or the first ~60 chars of the message).
 * Gated behind ENABLE_INTELLIGENT_INGEST; only emitted when the flag is on.
 */
export const DepositAckPayloadSchema = z.object({
  taskId: z.string(),
  /** Client-minted envelope uuid echoed back for idempotent deduplication. */
  uuid: z.string().optional(),
  /** Short label describing the deposited content. */
  label: z.string(),
});

export type DepositAckPayload = z.infer<typeof DepositAckPayloadSchema>;

// =============================================================================
// Nami Keeper (PR1) — the sweep terminator
// =============================================================================

/**
 * `agent:reconstruction_mismatch` — the wire-vs-journal reconstruction invariant
 * found the provider history the round was about to SEND differing from what the
 * journal re-derives through the canonical projection
 * (`project()` → `wireMessages` → `historyFromJournal`).
 *
 * Emitted at most ONCE per turn (the engine dedupes on the same latch as its
 * console line), SERVER-CONSUMED — never forwarded (forward: false in the
 * registry) — and persisted as a durable `error_events` row.
 *
 * REDACTION: the two heads are the first 200 chars of the stable JSON of the
 * first diverging element and nothing else. They exist to name the KIND of
 * divergence (an elided stub vs a raw body, a missing message, a reordered
 * block); they are deliberately too small to carry a transcript.
 */
export const AgentReconstructionMismatchPayloadSchema = z.object({
  taskId: z.string(),
  sessionId: z.string(),
  /** The round the check ran for (1-based within the turn). */
  round: z.number().int().nonnegative(),
  firstDivergingIndex: z.number().int().nonnegative(),
  incrementalLength: z.number().int().nonnegative(),
  projectedLength: z.number().int().nonnegative(),
  /** Capped at 200 chars by the engine; `<absent>` when that side is shorter. */
  incrementalHead: z.string(),
  projectedHead: z.string(),
});

/**
 * `agent:history_healed` — the provider rejected a model call's re-derived history
 * (HTTP 400 `invalid_request_error`, before any output, not a credit/context/image
 * 400) and the engine retried that call ONCE on a safe view: past tool rounds as plain
 * text, the latest round kept structured when well-formed (`tail_kept`) or everything
 * as text (`text_only`). Live prod task 28edef30 (2026-09-28) failed four turns on
 * one such rejection before this existed.
 *
 * `healed: true` at most once per turn (the retry succeeded); `healed: false` when the
 * retry was rejected too and the turn ended as before. SERVER-CONSUMED — never
 * forwarded (forward: false in the registry) — and persisted as an `error_events` row.
 *
 * REDACTION: no conversation text. `fingerprint` is the rejected request's per-message
 * roles and tool ids (toolBlockFingerprint), capped at 1000 chars by the engine.
 */
export const AgentHistoryHealedPayloadSchema = z.object({
  taskId: z.string(),
  sessionId: z.string(),
  healed: z.boolean(),
  view: z.enum(['tail_kept', 'text_only']),
  /** The SDK's typed error (always `invalid_request_error` today). */
  errorType: z.string(),
  /** The provider's request id for the REJECTED call, when the SDK carried one. */
  requestId: z.string().nullable(),
  /** The transport's wire dialect (`anthropic_messages`, `openai_responses`, …). */
  api: z.string(),
  model: z.string(),
  fingerprint: z.string(),
});

/**
 * `agent:hook_stop_continued` — X1 (HOOK_PARITY_V1): a Stop hook returned
 * `decision:"block"` (or exited 2), so the engine drove another round with the reason
 * as the next instruction instead of ending the turn. Each such round bills, so each
 * leaves a durable `error_events` row (category `hook_stop_continued`). SERVER-CONSUMED,
 * never forwarded. The reason is hook-authored text from a trusted system ability.
 */
export const AgentHookStopContinuedPayloadSchema = z.object({
  taskId: z.string(),
  /** Which consecutive continuation this is (1..3, the engine's cap). */
  n: z.number().int().positive(),
  /** The hook's block reason (its stderr on exit 2), capped at 500 chars by the engine. */
  reason: z.string(),
});

/**
 * Sandbox → server: the hot end-of-task memory pass is over, and every
 * NAMI_REMEMBER_DEPOSIT frame it produced has already crossed the wire (one
 * socket, synchronous emits, so this frame is strictly last). The server's fold
 * hangs off this — it is the ONLY end-of-capture signal; every other trigger
 * fires a whole model turn before any deposit exists.
 *
 * SERVER-CONSUMED — never forwarded (forward: false in the registry).
 */
export const NamiSweepCompletePayloadSchema = z.object({
  taskId: z.string(),
  /**
   * Deposits emitted by THIS sweep. POST-cap (parseMemorySweep stops at 30) and
   * POST-dedup: the count of frames actually emitted, NOT the count of FACT
   * lines the model produced. `0` is a first-class value, not a degenerate case
   * — it is what a turn that never staged a sweep at all reports, and it is what
   * keeps the fold reachable for the many tasks that never sweep.
   */
  deposited: z.number().int().nonnegative(),
});

// The inferred payload TYPE lives in types/events.ts (`NamiSweepCompletePayload`),
// with the other z.infer'd wire payloads — this file authors the schema only.

/**
 * Server → frontend: one thing Nami wants to tell the user out of band
 * (A3-T2 Reach v1). Mirrors the nami_notifications row that was just written
 * and cleared the gate; `id` addresses that row, so the surface that renders
 * this frame can mark it seen or dismissed without a second lookup.
 *
 * The length caps are the CONTRACT, not a hint: the copy is composed upstream
 * and this frame is what a toast or a native banner has to fit. `deepLink` is
 * app-relative ('/home', '/task/<id>') so the same string works in the web
 * app and inside the desktop shell.
 *
 * `politeness` rides along because the receiving surface, not the server,
 * decides how loud to be: 'may-notify' may raise a banner, while
 * 'silent-unless-actionable' is an in-app toast at most.
 */
export const NamiNotifyPayloadSchema = z.object({
  id: z.string(),
  // Which producer minted it. WIDENS with the producer set, and every widening has
  // to land here BEFORE the producer ships: the receiving surface parses the frame
  // against this schema before it renders, so an unknown kind is not a degraded
  // render, it is a DROPPED notification over a durable row nobody is ever shown.
  kind: z.enum(['prep_pack', 'initiative', 'ask', 'brief', 'digest', 'routine_hold', 'composer', 'file_shared']),
  title: z.string().max(120),
  body: z.string().max(400),
  // APP-RELATIVE, enforced. The receiving surface hands this string to the
  // router AND to the desktop shell's native banner, so an absolute URL here
  // would be an off-app navigation minted by whatever wrote the row. The frame
  // is emitted without server-side validation, which makes the consumer's parse
  // the only gate there is — so every field has to be checked in it, not just
  // the ones with obvious shapes.
  //
  // The negative lookahead is the whole point of writing it out: a bare `^\/`
  // would admit '//evil.example.com', which browsers resolve as a
  // PROTOCOL-RELATIVE absolute URL — the exact off-app navigation this is here
  // to refuse, wearing a leading slash.
  deepLink: z.string().regex(/^\/(?!\/)/, 'deepLink must be app-relative'),
  politeness: z.enum(['may-notify', 'silent-unless-actionable', 'draft-only']),
  createdAt: z.string(),
  // Spec 2026-09-18 §5.4: the task a `composer` notice is about, when it is about exactly one.
  // The surface drops the toast when that task is the one on screen (the only silence there is),
  // and gives the composer's history row its door. Optional: every other producer's frame, and
  // every frame an older server emits, is the frame it always was.
  taskId: z.string().max(64).optional(),
});

// The inferred payload TYPE lives in types/events.ts (`NamiNotifyPayload`).

/**
 * Browser → server: the screen this socket is showing (spec 2026-09-18, R-L1).
 * `view` is the destination id the path is on, `activeTaskId` the task on screen
 * (absent off a task route), `visible` whether the tab is visible. The server
 * keeps the last one ON THE SOCKET and reads it at one point only: a task that
 * rests, fails or asks while its person is looking at it says nothing. The
 * caps mirror the Line's context door, which the server validates it through.
 * Server-terminal: never forwarded, never emitted back.
 */
export const ComposerScreenPayloadSchema = z.object({
  view: z.string().max(64).optional(),
  activeTaskId: z.string().max(64).optional(),
  visible: z.boolean(),
});

// The inferred payload TYPE lives in types/events.ts (`ComposerScreenPayload`).

// =============================================================================
// Agent Teams (P5.1) — teammate spawn + cross-lane message
// =============================================================================

/**
 * A teammate task was spawned via the Agent tool (mode:'teammate'). The parent
 * task is the render host; `teammateTaskId` is the child's own task id.
 * Dark behind the AGENT_TEAMS flag. Mirrors SubagentSpawnedPayload field-for-field.
 */
export const SubagentSpawnedPayloadSchema = z.object({
  taskId: z.string(),
  toolUseId: z.string(),
  teammateTaskId: z.string(),
  name: z.string(),
  agentSlug: z.string(),
  agentName: z.string().optional(),
  agentIcon: z.string().optional(),
  agentColor: z.string().optional(),
  model: z.string().optional(),
  parentTaskId: z.string(),
  // I-2 (P5.2 review) — arm pinned at spawn; additive-optional (absent pre-P5.2).
  runtime: z.string().optional(),
  // Phase 2 (spec §4.7) — 'helper' = a background one-shot helper; absent = a named
  // teammate. Declared here or the relay's safeParse STRIPS it before forwarding.
  kind: z.enum(['teammate', 'helper']).optional(),
  // Phase 2 — the Agent call's `description`, which titles a helper's lane + report.
  description: z.string().optional(),
  // Phase 3 (spec 2026-09-05 §4.6) — the person can message this helper, which is what
  // the composer's @ list reads to offer it (never a handle's shape). Declared here
  // because the relay's safeParse is what the durable teammate registration reads, and a
  // plain z.object STRIPS what it does not declare; the frontend forward leg spreads the
  // raw frame, so a field missing here reaches the web but nothing server-side.
  addressable: z.boolean().optional(),
});

/**
 * A message between a lead and a teammate. `taskId` is the persist-target
 * (recipient's thread); `peerTaskId` is the other side. Mirrors
 * SubagentMessagePayload field-for-field.
 */
export const SubagentMessagePayloadSchema = z.object({
  taskId: z.string(),
  direction: z.enum(['to_teammate', 'to_lead']),
  name: z.string(),
  peerTaskId: z.string(),
  text: z.string(),
  messageId: z.string(),
  // Phase 2 (spec §4.7, D11) — why a background helper's report arrived (turn cap,
  // stopped by the user, failed). Same declare-or-stripped contract as above.
  // LENIENT ON PURPOSE (R89), unlike the `kind` enum above: the TYPE is the four-value
  // SubagentReportStopReason, but a helper ALWAYS reports, and a reason this build does
  // not know must never cost the lead the report BODY, which is the helper's result.
  stopReason: z.string().optional(),
  // Phase 3 (spec §4.5) — the SENDER's handle (`lead` for the lead). A plain
  // z.object STRIPS unknown keys, so without this line the relay would drop the
  // handle and every helper's message would read as the lead's.
  from: z.string().optional(),
});

// =============================================================================
// Session Branch (Phase 6)
// =============================================================================

// =============================================================================
// Cobrowsing P2 — presence & stream lifecycle (BROWSER_VIEWER_LEASE, dark)
// =============================================================================

/**
 * Frontend → server: viewer-presence heartbeat, sent every ~5 s while the live
 * view's Neko client is connected and its tab is on screen (apps/web
 * hooks/useBrowserViewingBeat.ts). Carries no data — the socket's auth
 * context identifies the viewer; `.strict()` so stray fields fail loudly.
 */
export const BrowserViewingPayloadSchema = z.object({}).strict();

/**
 * Server → sandbox: computer-level wake (desktop lock screen,
 * BROWSER_WAKE{target:'computer'}). The sandbox holds itself awake
 * (wakeGrant, same grant as a human browser wake) and touches nothing else —
 * no Chrome, no session, no announce.
 */
export const ComputerWakePayloadSchema = z.object({
  wakeGrant: z.boolean().optional(),
  /** A /v1 warm's keep_ms (apps/server platform/compute/warm.ts): hold the box awake this long, at most two minutes. */
  holdMs: z.number().int().positive().max(120_000).optional(),
});

/**
 * Frontend → server, and server → sandbox: the human put the computer to
 * sleep (desktop menubar power glyph). Deliberately empty — the event IS the
 * payload; `.strict()` so stray fields fail loudly.
 */
export const ComputerSleepPayloadSchema = z.object({}).strict();

/**
 * Frontend → server: the browser-view PULL — "what is the view right now?".
 * Deliberately empty: the socket's auth context names the user and the asking
 * socket is the one that gets the answer, so the event IS the payload.
 * `.strict()` so a stray field (a caller trying to smuggle in an intent this
 * question must never carry, such as a wake) fails loudly.
 */
export const BrowserViewSyncPayloadSchema = z.object({}).strict();

/**
 * Sandbox → server → frontend (forwarded): the agent opens the user's
 * computer view on a surface — or on one named creation, full screen, when
 * the piece's identity rides along (from present_output's own result). A
 * view verb only: it never starts, stops, or wakes anything.
 */
export const ComputerShowPayloadSchema = z.object({
  taskId: z.string().min(1),
  surface: z.enum(['browser', 'files', 'app', 'terminal', 'previews']),
  /** The creation to open full screen (all three from present_output's result). */
  name: z.string().max(200).optional(),
  url: z.string().max(2048).optional(),
  id: z.string().max(64).optional(),
});

/** Sandbox → server → frontend (forwarded): the agent closes the computer window. */
export const ComputerClosePayloadSchema = z.object({
  taskId: z.string().min(1),
});

/**
 * Frontend → server (acked): connection-heartbeat RTT probe for the client
 * health rail. Deliberately empty — the ack's round-trip time IS the payload;
 * `.strict()` so stray fields fail loudly.
 */
export const ConnectionHeartbeatPayloadSchema = z.object({}).strict();

/**
 * Server → frontend: the viewer lease lapsed and the stream was suspended.
 * Mirrors BrowserStreamSuspendedPayload (types/browser-stream.ts).
 */
export const BrowserStreamSuspendedPayloadSchema = z.object({
  sessionId: z.string(),
  reason: z.enum(['idle', 'disconnect']),
});

/**
 * Server → frontend: a viewer returned and the suspended stream resumed.
 * Mirrors BrowserStreamResumedPayload (types/browser-stream.ts).
 */
export const BrowserStreamResumedPayloadSchema = z.object({
  sessionId: z.string(),
});

/**
 * Sandbox → server ([ow1] Cobrowsing P3 telemetry): the shared-control guard
 * refused an agent action because the human was still driving at the wait cap.
 * Server-consumed only (cobrowse-telemetry.ts), never forwarded.
 */
export const BrowserControlDeferredPayloadSchema = z.object({
  taskId: z.string(),
});

/**
 * Frontend → server → sandbox: a navigation control in the embedded browser
 * pane's chrome (back / forward / reload / goto / activate_tab / new_tab).
 * Mirrors BrowserNavigatePayload (types/browser-stream.ts). The runner is the
 * enforcement point: transport gating (attached local/embedded browser only)
 * and the nav-guard screen on goto/back/forward destinations happen there.
 */
export const BrowserNavigatePayloadSchema = z.object({
  taskId: z.string(),
  action: z.enum(['back', 'forward', 'reload', 'goto', 'activate_tab', 'new_tab']),
  url: z.string().optional(),
  targetId: z.string().optional(),
});

/**
 * Frontend → server: is a human still looking at a pane this box serves (work
 * holds the box, spec §5.1). Mirrors BrowserPaneVisibilityPayload
 * (types/browser-stream.ts). Server-terminal — never forwarded to the sandbox,
 * never emitted back down.
 */
export const BrowserPaneVisibilityPayloadSchema = z.object({
  visible: z.boolean(),
});

/**
 * Sandbox → server → frontend: the embedded browser chrome's tab strip
 * (BROWSER_TABS, local/embedded lane only). Mirrors BrowserTabsPayload
 * (types/browser-stream.ts): full snapshot, every tab stamped with `active`,
 * top-level taskId = the task owning the active tab ('' when none).
 */
export const BrowserTabsPayloadSchema = z.object({
  taskId: z.string(),
  tabs: z.array(z.object({
    targetId: z.string(),
    url: z.string(),
    title: z.string(),
    favicon: z.string().nullable(),
    taskId: z.string().nullable(),
    active: z.boolean(),
  })),
});

/**
 * Sandbox → server (BROWSER_MIRROR_V1): a batch of rrweb recorder events captured
 * from the in-sandbox browser. Mirrors BrowserMirrorEventPayload
 * (types/browser-stream.ts). The server owns a dedicated buffered fan-out handler
 * (a ring buffer of the current segment that replays to a late-joining panel), so
 * this event is NOT on the generic forward path (forward:false), exactly like
 * BROWSER_VIEWING. `seq` is a monotonic per-recorder batch counter (a gap ⇒ a
 * dropped batch); `events` are raw rrweb event JSON strings as the recorder
 * serialized them (the panel JSON.parses each before Replayer.addEvent).
 */
export const BrowserMirrorEventPayloadSchema = z.object({
  sessionId: z.string(),
  seq: z.number().int().nonnegative(),
  events: z.array(z.string()),
});

/**
 * Server → sandbox (BROWSER_MIRROR_V1): request the in-sandbox recorder to emit a
 * fresh rrweb full snapshot (rrwebRecord.takeFullSnapshot). Sent when the server's
 * per-user segment buffer is empty / stale / gapped and a panel needs to bootstrap,
 * closing #1895's gap where the initial snapshot can be dropped while the relay
 * socket is null. The sandbox no-ops it unless the recorder is armed. `sessionId`
 * echoes the recorder's session id (the neko constant) for symmetry with the
 * inbound BROWSER_MIRROR_EVENT; the sandbox has a single recorder so it is
 * informational, not a selector.
 */
export const BrowserMirrorResnapshotPayloadSchema = z.object({
  sessionId: z.string(),
});

/**
 * Server → brain (Composer Eyes): run one read-only OS perception op against the
 * local VestraHands sidecar. `op` mirrors OsPerceiveOp
 * (computer-use/os-hands-protocol.ts): apps/snapshot/find plus the phase-SEE
 * reads selection/pointer/read_text/ocr. Mirrors OsPerceiveRequest; requestId
 * correlates the reply. Server ↔ brain only, never forwarded.
 */
export const OsPerceiveRequestPayloadSchema = z.object({
  requestId: z.string(),
  op: z.enum(['apps', 'snapshot', 'find', 'selection', 'pointer', 'read_text', 'ocr']),
  args: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Brain → server (Composer Eyes, Slice 1): the perception result echoing the
 * same requestId (resolved server-side via awaitSandboxEvent). Mirrors
 * OsPerceiveResult (computer-use/os-hands-protocol.ts).
 */
export const OsPerceiveResultPayloadSchema = z.object({
  requestId: z.string(),
  ok: z.boolean(),
  text: z.string().optional(),
  // `apps` op only: the structured running-app roster (frontmost = active), so a
  // consumer reads the target from a field instead of re-parsing `text`.
  apps: z
    .array(
      z.object({
        bundleId: z.string().optional(),
        name: z.string().optional(),
        active: z.boolean().optional(),
      }),
    )
    .optional(),
  error: z.string().optional(),
});

/**
 * Server → brain (Tier-0 verbs, Composer lane): run one registry verb inline
 * (taskless). Mirrors OsVerbRequest (computer-use/os-hands-protocol.ts);
 * requestId correlates the reply. Server ↔ brain only, never forwarded.
 */
export const OsVerbRequestPayloadSchema = z.object({
  requestId: z.string(),
  verb: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Brain → server (Tier-0 verbs): the verb outcome echoing the same requestId
 * (resolved server-side via awaitSandboxEvent). `code` carries the structured
 * VERB_* code when ok=false. Mirrors OsVerbResult.
 */
export const OsVerbResultPayloadSchema = z.object({
  requestId: z.string(),
  ok: z.boolean(),
  output: z.string().optional(),
  error: z.string().optional(),
  code: z.string().optional(),
});

/** Sandbox → server: the box's sign-in state for one plan engine's account. null = not connected. */
export const EngineAuthStatusPayloadSchema = z.object({
  engine: z.enum(SUBSCRIPTION_ENGINES),
  status: z.enum(['connected', 'needs_login']).nullable(),
  /** The signed-in account, when the CLI reports one. Shown in Settings; never a credential. */
  account: z.string().max(320).optional(),
  /**
   * The plan tier the CLI named (`auth status --json` subscriptionType: 'max', 'pro', ...),
   * sent as-is (engines T2b). Absent = not named, never "no plan": the server keeps what it knew.
   */
  tier: z.string().max(64).optional(),
  /** The account's slot (spec 2026-10-01). Absent = 'primary', as every box before it. */
  slotId: SlotIdSchema.optional(),
  /**
   * The signed-in account's organisation, as `auth status --json` names it (spec 2026-10-01 §5.2,
   * shown beside the email). Absent = not named: the server keeps what it knew.
   */
  orgName: z.string().max(320).optional(),
});

/** Server → sandbox: sign this engine out (one slot; absent = 'primary'). */
export const EngineAuthLogoutPayloadSchema = z.object({ engine: z.enum(SUBSCRIPTION_ENGINES), slotId: SlotIdSchema.optional() });

/**
 * One line for the person in one task (engines T0). Sandbox → server → frontend when the
 * runner authors it (a plan heads-up); the relay composes one from every PERMISSION_DENIED.
 * The relay buffers it, so the turn persists it as a `notice` block.
 */
export const TaskNoticePayloadSchema = NoticeBlockSchema.omit({ type: true }).extend({
  taskId: z.string().min(1),
  reading: PlanHeadsUpReadingSchema.optional(), // a plan heads-up only, so the relay can name the account; never persisted
});

/**
 * The gate refused a tool call (sandbox → server → frontend). The deny tool_result the model
 * gets is unchanged. `reason` is the gate's sentence FOR THE MODEL and is never shown to a
 * person; `hint` is a pointer written for people (an engine's own tool refused by name);
 * `notice` is the line the relay composes from these before it forwards the frame.
 */
export const PermissionDeniedPayloadSchema = z.object({
  taskId: z.string(),
  toolName: z.string(),
  toolUseId: z.string(),
  reason: z.string(),
  reasonType: z.string().optional(),
  hint: z.string().optional(),
  notice: TaskNoticePayloadSchema.optional(),
});

/** One plan window as the box read it. A missing number is null, never 0. */
const StandingWindowSchema = z.object({
  kind: z.enum(STANDING_WINDOW_KINDS),
  label: z.string().max(64).optional(),
  usedPct: z.number().min(0).max(100).nullable(),
  resetsAt: z.string().datetime().nullable(),
});

/**
 * Sandbox → server: where the person stands on their own plan (engines spec §6.8). Persisted
 * to engine_accounts. Never forwarded: Settings reads the stored value over HTTP, so a
 * sleeping box never has to answer.
 */
export const EngineStandingPayloadSchema = z.object({
  /** The task whose turn read it. Absent for a pull (engines T2b): Settings asked, no task did. */
  taskId: z.string().min(1).optional(),
  standing: z.object({
    engine: z.enum(SUBSCRIPTION_ENGINES),
    tier: z.string().max(64).nullable(),
    windows: z.array(StandingWindowSchema).max(8),
    status: z.enum(STANDING_STATUSES),
    source: z.enum(['stream', 'pull']),
    measuredAt: z.string().datetime(),
    /** The slot whose plan this is (EngineStanding.slotId, spec 2026-10-01). Absent = 'primary'. */
    slotId: SlotIdSchema.optional(),
  }),
});

/**
 * Server → sandbox: read where the person stands now, off the engine itself (engines T2b), and
 * report it as an ENGINE_STANDING with source 'pull'. Sent only to a box that is already awake.
 */
export const EngineStandingRefreshPayloadSchema = z.object({
  engine: z.enum(SUBSCRIPTION_ENGINES),
  /** One slot to pull (spec 2026-10-01). Absent = every slot the box holds, one at a time. */
  slotId: SlotIdSchema.optional(),
});

/** Server → sandbox: Settings → Connect. The web mints connectId; every frame carries it back.
 *  `slotId` names the account being signed in (spec 2026-10-01); absent = 'primary'. */
export const EngineConnectPayloadSchema = z.object({
  engine: z.enum(SUBSCRIPTION_ENGINES),
  connectId: z.string().uuid(),
  slotId: SlotIdSchema.optional(),
  /**
   * The person's account rows as the server holds them (several accounts; the truth, never the
   * box's memory): the box refuses a sign-in landing on ANOTHER row's email, and a Reconnect hints
   * its own row's. Absent (an older server, no rows yet): the box looks for itself.
   */
  accounts: z.array(z.object({ slotId: SlotIdSchema, email: z.string().max(320).nullable() })).max(16).optional(),
  /** The slot's row says signed out (a placeholder Add, a removed primary): sign it out on the box before the sign-in. */
  signOutFirst: z.boolean().optional(),
  /** How to sign in (engines T3): the vendor's page in the box's browser, or a code. Absent = 'browser'. */
  method: z.enum(ENGINE_SIGN_IN_METHOD_IDS).optional(),
});

/** Server → sandbox: end THIS connect (the person cancelled or left Settings). */
export const EngineConnectCancelPayloadSchema = z.object({
  engine: z.enum(SUBSCRIPTION_ENGINES),
  connectId: z.string().uuid(),
  slotId: SlotIdSchema.optional(),
});

/**
 * Box → server → the person's sockets: one phase of a Settings connect. `account` rides
 * `connected` (never a credential); `reason` rides `failed`; `verificationUrl` and `userCode`
 * ride `code` (the page to open and the code to enter there; a one-time code, never a credential).
 */
export const EngineConnectProgressPayloadSchema = z.object({
  engine: z.enum(SUBSCRIPTION_ENGINES),
  connectId: z.string().uuid(),
  phase: z.enum(ENGINE_CONNECT_PHASES),
  account: z.string().max(320).nullable().optional(),
  reason: z.enum(ENGINE_CONNECT_FAILURES).optional(),
  /** The slot being connected (spec 2026-10-01); absent = 'primary'. */
  slotId: SlotIdSchema.optional(),
  verificationUrl: z.string().url().optional(),
  userCode: z.string().max(32).optional(),
});

/**
 * Server → sandbox: the person's live slots for one engine (spec 2026-10-01; every row the server
 * holds, a signed-out primary included). The box deletes every other slot dir it holds. A frame
 * that does not parse is dropped, so a malformed list can never read as "delete everything".
 * The bound is generous (a person holds at most five): it only refuses garbage.
 */
export const EngineSlotsSyncPayloadSchema = z.object({
  engine: z.enum(SUBSCRIPTION_ENGINES),
  slotIds: z.array(SlotIdSchema).max(16),
});

// Phase 9: Elicitation Zod schemas deleted — the elicitation/ module was
// removed; MCP elicitation now flows through the unified INTERACTION_REQUIRED
// gateway with kind:'custom' (see types/interaction.ts).

// =============================================================================
// Schema Map for Dynamic Validation
// =============================================================================
// The { name → schema } EventSchemas map (and its EventSchemaKey type) is now
// DERIVED from the single authored wire-contract table in ../events/registry.ts
// and re-exported from ../events/index.ts and schemas/index.ts. It is no longer
// hand-assembled here — the registry is the sole author, so the map can never
// drift from the schema set. See events/registry.ts.

// =============================================================================
// Type Exports (inferred from schemas)
// =============================================================================

export type SessionCreatePayloadInput = z.input<typeof SessionCreatePayloadSchema>;
export type UserMessagePayloadInput = z.input<typeof UserMessagePayloadSchema>;
export type AgentTextPayloadInput = z.input<typeof AgentTextPayloadSchema>;
export type AgentToolUsePayloadInput = z.input<typeof AgentToolUsePayloadSchema>;
